import {clamp, MiniSignal, padNum, performNextTick, random, texture} from "p3";
import definitions from "../../../definitions";
import GameScene from "../../../scenes/GameScene";
import Enemy from "./Enemy";

export default class EnemyChicken extends Enemy {
    constructor() {
        super(definitions.ENEMY_ID.CHICKEN);

        this.moveSpeed = 1000;
        this.linearDamping = 6;
        this.collisionDamping = 12;

        this.collisionRadius = 17;
        this.collisionOffsetX = -5;
        this.collisionOffsetY = -12;

        this.moveToTarget = null;
        this.moveToTargetOffset = new PIXI.Point();
        this.moveToTargetMinRadius = 80;
        this.moveToTargetMaxRadius = 120;

        this.moveAwayFromTarget = null;
        this.moveAwayFromTargetOffset = new PIXI.Point();
        this.moveAwayFromTargetMinRadius = 0;
        this.moveAwayFromTargetMaxRadius = 600;

        this._retreatSpeed = 1800;

        this._avoidNeighbourMinRange = 140;
        this._avoidNeighbourWeight = 0.6;

        this._defaultTween = null;

        this.interactable = false;
        this.interactionRadius = 140;

        this.signals.grabbed = new MiniSignal();
    }

    init() {
        super.init();

        let frames = [];
        for (let i = 0; i < 2; ++i) {
            frames[i] = texture("char_chicken-idle_00_" + String(i));
        }
        const sequence = new P3.MovieClipSequence(frames, "idle");

        frames = [];
        for (let i = 0; i < 6; ++i) {
            frames[i] = texture("char_chicken-walk_r_00_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "move");

        frames = [];
        for (let i = 0; i < 6; ++i) {
            frames[i] = texture("char_chicken-caught_r_00_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "caught");

        /*
        frames = [];
        for (let i = 0; i < 12; ++i) {
            frames[i] = texture("enemy_flying_snake-attack_00_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "attack");

        frames = [];
        for (let i = 0; i < 12; ++i) {
            frames[i] = texture("enemy_flying_snake-take_hit_00_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "take_damage");

        frames = [];
        for (let i = 0; i < 12; ++i) {
            frames[i] = texture("enemy_flying_snake-defeat_00_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "dead");
        */
        this.addViewWithMovieClip(sequence, "idle");
    }

    destroy() {
        this._stopAnimations();

        super.destroy();
    }

    onViewAdded() {
        super.onViewAdded();

        this._view.scale.set(0.6);
        this._view.anchor.set(0.5, 1);
        this._view.gotoAndPlay("idle", true);

        this._view.addFrameScript(4, () => this.onAttackDamage(), null, "attack");
        this._view.addFrameScript(11, () => this.onAttackFinished(), null, "attack");
        this._view.addFrameScript(11, () => this.onTakeDamageFinished(), null, "take_damage");

        this.addShadow(definitions.SHADOW_SIZE.SMALL, 1, new PIXI.Point(-3, -10));
    }


    takeDamage(amount) {
        return false;
    }

    update() {
        super.update();

        if (this.velocity.lengthSq > 4 ** 2) {
            if (!this._isMoving) {
                this._isMoving = true;
                this.onMoveStart();
            }
        } else {
            if (this._isMoving) {
                this._isMoving = false;
                this.onMoveStop();
            }
        }
    }

    /**
     * @return {PIXI.Point}
     * @private
     */
    _findSafePosition() {
        return this.world.findSafePosition(
            new PIXI.Point(this._initialPosition.x, this._initialPosition.y),
            600,
            0,
            120,
            [new PIXI.Point(this.x, this.y)],
            20
        );
    }

    /**
     * @param {object} target
     * @param {P3.Vector2} force
     * @private
     */
     _moveToTarget(target, force) {
        let move = true;

        force.set(target.x - this.x, target.y - this.y);
        const distance = force.length;
        force.x /= distance;
        force.y /= distance;

        let targetSpeed = Math.min(1, (distance / this.moveToTargetMaxRadius)) * this.moveSpeed;
        if (distance < this.moveToTargetMinRadius) {
            move = false;
            targetSpeed = 0;
        }
        force.scaleBy(targetSpeed);
        return move;
    }

        /**
     * @param {object} target
     * @param {P3.Vector2} force
     * @private
     */
    _moveAwayFromTarget(target, force) {
        let move = true;

        force.set(this.x - target.x, this.y - target.y);
        const distance = force.length;
        //const inverse = Math.min(this.moveAwayFromTargetMaxRadius, Math.max(this.moveAwayFromTargetMinRadius, this.moveAwayFromTargetMaxRadius - force.length));
        force.x /= distance;
        force.y /= distance;

        let targetSpeed = Math.max(0, Math.min(1, 1-(distance / this.moveAwayFromTargetMaxRadius))) * this.moveSpeed;
        if (distance < this.moveAwayFromTargetMinRadius) {
            move = false;
            targetSpeed = 0;
        }
        force.scaleBy(targetSpeed);
        return move;
    }

    /**
     * @param {P3.Vector2} force
     * @param {number} minRange
     * @param {number} [weight = 0.5]
     * @private
     */
    _avoidNeighbours(force, minRange, weight = 0.5) {
        weight = Math.max(0, weight);
        for (let i = 0, count = this.neighbours.length; i < count; ++i) {
            const neighbour = this.neighbours[i];
            if (this !== neighbour) {
                if (!(this.x === neighbour.x && this.y === neighbour.y)) {
                    const to = new P3.Vector2(this.x - neighbour.x, this.y - neighbour.y);
                    const distance = to.length;
                    if (distance < minRange) {
                        const speed = (1 - clamp(distance / minRange, 0, 1)) * minRange;
                        to.x /= distance;
                        to.y /= distance;
                        force.x += to.x * speed * weight;
                        force.y += to.y * speed * weight;
                    }
                }
            }
        }
    }

    _stopAnimations() {
        if (this._defaultTween) {
            GameScene.active.removeTween(this._defaultTween, true);
            this._defaultTween = null;
        }
    }

    
    onMoveStart() {
        if (!this.isAttacking && !this.isTakingDamage && !this.isDead && this.state == definitions.ENEMY_STATE.RETREAT) {
            this._view.gotoAndPlay("move", true);
        }
    }

    onMoveStop() {
        if (!this.isAttacking && !this.isTakingDamage && !this.isDead && this.state == definitions.ENEMY_STATE.RETREAT) {
            this._view.gotoAndPlay("idle", true);
        }
    }

    /**
     * @param {Entity} entity
     * @param {P3.Vector2} v
     */
    onCollisionEntity(entity, v) {
        super.onCollisionEntity(entity, v);

        this.x += v.x;
        this.y += v.y;

        v.normalize();

        const d = 1 - Math.exp(-this.collisionDamping * window.dt);

        this.velocity.x *= 1 - Math.abs(v.x) * d;
        this.velocity.y *= 1 - Math.abs(v.y) * d;
    }

    /**
     * @param {Tile} tile
     * @param {P3.Vector2} v
     */
    onCollisionTile(tile, v) {
        super.onCollisionTile(tile, v);

        this.x += v.x;
        this.y += v.y;

        v.normalize();

        const d = 1 - Math.exp(-this.collisionDamping * window.dt);

        this.velocity.x *= 1 - Math.abs(v.x) * d;
        this.velocity.y *= 1 - Math.abs(v.y) * d;
    }

    /**
     * @param {number} state
     * @private
     */
    
    onStateEnter(state) {
        switch (state) {

            case definitions.ENEMY_STATE.RETREAT: {
                this.moveSpeed = this._retreatSpeed;
                this.moveAwayFromTarget = this.target;
                this._view.gotoAndPlay("move", true);
                //this.moveToTarget = this._findSafePosition();
                break;
            }
            case definitions.ENEMY_STATE.CAPTURE: {
                console.log('set capture');
                this._view.gotoAndPlay("caught", true);
                this._view.animationSpeed = 24;
                break;
            }
            case definitions.ENEMY_STATE.IDLE: {
                this._view.gotoAndPlay("idle", true);
                this._view.animationSpeed = 6;
                break;
            }
        }
    }

        /**
     * @param {number} state
     */
    onStateUpdate(state) {
        switch (state) {
            case definitions.ENEMY_STATE.RETREAT: {
                if (this.moveToTarget) {
                    const force = new P3.Vector2();
                    const target = new PIXI.Point(this.moveToTarget.x + this.moveToTargetOffset.x, this.moveToTarget.y + this.moveToTargetOffset.y);
        
                    this._moveToTarget(target, force);
                    this.facingDirection = force.x;
        
                    this._avoidNeighbours(force, this._avoidNeighbourMinRange, this._avoidNeighbourWeight);
                    this.velocity.set(this.velocity.x + force.x * window.dt, this.velocity.y + force.y * window.dt);
                }
                else if (this.moveAwayFromTarget) {
                    const force = new P3.Vector2();
                    const target = new PIXI.Point(this.moveAwayFromTarget.x + this.moveAwayFromTargetOffset.x, this.moveAwayFromTarget.y + this.moveAwayFromTargetOffset.y);
        
                    const distance = Math.sqrt(Math.pow(Math.abs(this.x - target.x), 2) + Math.pow(Math.abs(this.y - target.y), 2));
                    if (distance > this.moveAwayFromTargetMinRadius && distance < this.moveAwayFromTargetMaxRadius){
                        this._moveAwayFromTarget(target, force);
                    }
                    
                    this.facingDirection = force.x;
        
                    this._avoidNeighbours(force, this._avoidNeighbourMinRange, this._avoidNeighbourWeight);
                    this.velocity.set(this.velocity.x + force.x * window.dt, this.velocity.y + force.y * window.dt);

                    this._view.animationSpeed = Math.max(6, 24 * (this.velocity.length/(this.moveSpeed/4)));

                    const d = new P3.Vector2(this.x - target.x, this.y - target.y);
                    if (d.length < 50) {
                        this.signals.grabbed.dispatch(this);
                    }
                }
                break;
            }
            case definitions.ENEMY_STATE.CAPTURE: {
                this.x = this.target.x + (this.target.holdingOffset.x * this.target.facingDirection);
                this.y = this.target.y + this.target.holdingOffset.y;
                if(this._view.currentAnimationFrame !== "caught")
                    this._view.gotoAndPlay("caught", true);
                break;
            }
        }
    }

}